import React, { useState, useEffect } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import supplierService from '../../services/supplierService';
import EquipmentStatusCard from './components/EquipmentStatusCard';
import AssignedCaseCard from './components/AssignedCaseCard';
import ProductionSchedulePanel from './components/ProductionSchedulePanel';
import QualityControlPanel from './components/QualityControlPanel';

const ProductionSupplierInterface = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [machines, setMachines] = useState([]);
  const [caseAssignments, setCaseAssignments] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Interface Fournisseur', path: '/production-supplier-interface', icon: 'Factory' }
  ];

  useEffect(() => {
    if (user?.id) {
      fetchSupplierData();
    }
  }, [user]);

  const fetchSupplierData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch only machines assigned to this supplier
      const machinesData = await supplierService?.getSupplierMachines(user?.id);
      setMachines(machinesData);

      // Fetch only cases assigned to this supplier
      const casesData = await supplierService?.getSupplierCaseAssignments(user?.id);
      setCaseAssignments(casesData);

      // Fetch supplier performance metrics
      const performanceData = await supplierService?.getSupplierPerformance(user?.id);
      setPerformance(performanceData);
    } catch (err) {
      console.error('Error fetching supplier data:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleMachineStatusUpdate = async (machineId, status) => {
    try {
      // Update machine status logic here
      await fetchSupplierData();
    } catch (err) {
      console.error('Error updating machine status:', err);
    }
  };

  const handleCaseUpdate = async (caseId, updateData) => {
    try {
      await supplierService?.updateCaseAssignment(caseId, updateData);
      await fetchSupplierData();
    } catch (err) {
      console.error('Error updating case:', err);
    }
  };

  const activeMachines = machines?.filter(m => m?.status === 'operational')?.length || 0;
  const pendingCases = caseAssignments?.filter(c => c?.status === 'assigned')?.length || 0;
  const inProgressCases = caseAssignments?.filter(c => c?.status === 'in_progress')?.length || 0;

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="container mx-auto px-4 py-6 max-w-7xl">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Interface Fournisseur Production</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Gestion de vos équipements et cas assignés
                </p>
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Machines Actives</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">{activeMachines}/{machines?.length}</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Icon name="Settings" size={24} className="text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Conceptions en Attente</p>
                    <p className="text-2xl font-bold text-orange-600 mt-1">{pendingCases}</p>
                  </div>
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <Icon name="Clock" size={24} className="text-orange-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">En Production</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">{inProgressCases}</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Icon name="Activity" size={24} className="text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Taux Utilisation</p>
                    <p className="text-2xl font-bold text-purple-600 mt-1">{performance?.utilizationRate || 0}%</p>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <Icon name="TrendingUp" size={24} className="text-purple-600" />
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <Icon name="AlertCircle" size={24} className="text-red-600 mx-auto mb-2" />
                <p className="text-red-600">{error}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Equipment & Cases */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Equipment Section */}
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b border-gray-200">
                      <h2 className="text-lg font-semibold text-gray-900">Mes Équipements</h2>
                      <p className="text-sm text-gray-600 mt-1">
                        Machines sous votre responsabilité
                      </p>
                    </div>
                    <div className="p-6">
                      {machines?.length === 0 ? (
                        <div className="text-center py-8">
                          <Icon name="Settings" size={48} className="text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600">Aucune machine assignée</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {machines?.map(machine => (
                            <EquipmentStatusCard
                              key={machine?.id}
                              machine={machine}
                              onStatusUpdate={handleMachineStatusUpdate}
                              onSelect={() => setSelectedMachine(machine)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Assigned Cases Section */}
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b border-gray-200">
                      <h2 className="text-lg font-semibold text-gray-900">Cas Assignés</h2>
                      <p className="text-sm text-gray-600 mt-1">
                        Cas de production alloués à votre atelier
                      </p>
                    </div>
                    <div className="p-6">
                      {caseAssignments?.length === 0 ? (
                        <div className="text-center py-8">
                          <Icon name="Briefcase" size={48} className="text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600">Aucun cas assigné</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {caseAssignments?.map(caseItem => (
                            <AssignedCaseCard
                              key={caseItem?.id}
                              caseAssignment={caseItem}
                              onUpdate={handleCaseUpdate}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Column - Schedule & Quality */}
                <div className="space-y-6">
                  <ProductionSchedulePanel cases={caseAssignments} />
                  <QualityControlPanel performance={performance} />
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProductionSupplierInterface;